import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Disclaimer from "../components/Disclaimer";
import { BloodworkBadge } from "../components/Badges";
import { StartingAtPill, TierTotal } from "../components/Pricing";
import { CallCard } from "../components/PhoneCTA";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";

const AGES = ["18-29", "30-39", "40-49", "50-59", "60-69", "70+"];
const SEX = ["Male", "Female", "Prefer not to say"];
const STATES = ["NV", "NM", "FL", "WI", "NE", "Other"];

const SAFETY = [
  { id: "pregnant", label: "Pregnant or breastfeeding" },
  { id: "active_cancer", label: "Active or recent cancer" },
  { id: "diabetes", label: "Diabetes or prediabetes" },
  { id: "pancreatitis", label: "History of pancreatitis" },
  { id: "gallbladder", label: "Gallbladder disease" },
  { id: "kidney", label: "Kidney disease" },
  { id: "liver", label: "Liver disease" },
  { id: "cardiovascular", label: "Cardiovascular disease" },
  { id: "hypertension", label: "Uncontrolled hypertension" },
  { id: "psychiatric", label: "Severe psychiatric condition" },
  { id: "endocrine_tumor", label: "History of endocrine tumors" },
  { id: "med_allergy", label: "Known medication allergies" },
];

export default function Questionnaire() {
  const [step, setStep] = useState(1);
  const [goals, setGoalOptions] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [profile, setProfile] = useState({
    age_range: "", sex_at_birth: "", state: "",
    primary_concern: "", desired_outcome: "",
    has_recent_bloodwork: false, on_hormone_therapy: false, on_weight_medication: false,
  });
  const [safetyFlags, setSafetyFlags] = useState([]);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/goals").then(({ data }) => setGoalOptions(data)); }, []);

  const toggleGoal = (slug) => setSelectedGoals((p) => p.includes(slug) ? p.filter((s) => s !== slug) : (p.length >= 5 ? p : [...p, slug]));
  const toggleFlag = (id) => setSafetyFlags((p) => p.includes(id) ? p.filter((s) => s !== id) : [...p, id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/questionnaire/results", {
        goals: selectedGoals,
        ...profile,
        safety_flags: safetyFlags,
      });
      setResults(data);
      setStep(4);
    } finally { setSubmitting(false); }
  };

  const progress = (step / 4) * 100;

  return (
    <div data-testid="questionnaire-page" className="vv-container py-14">
      <SEO
        title="Find My Peptide Treatment — Symptom & Goal Questionnaire"
        description="Complete a safety-screened questionnaire and receive Beginner / Intermediate / Advanced educational peptide pathways a licensed VidaVital provider may evaluate."
        path="/find-my-treatment"
        breadcrumbs={[{ name: "Find My Treatment", path: "/find-my-treatment" }]}
      />
      <Breadcrumbs items={[{ name: "Find My Treatment", path: "/find-my-treatment" }]} />
      <div className="vv-hero-eyebrow">Find My Treatment</div>
      <h1 className="font-display text-4xl md:text-5xl mt-2">A few quick questions</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">This is not a diagnosis or complete medical intake. A provider will independently review all information.</p>

      <div className="mt-8 h-1.5 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full bg-[color:var(--vv-teal)] transition-all" style={{ width: `${progress}%` }}></div>
      </div>
      <div className="text-xs text-stone-500 mt-2">Step {step} of 4</div>

      {step === 1 && (
        <div className="mt-8">
          <h2 className="font-display text-2xl">Which goals matter most? <span className="text-sm text-stone-500 font-sans">(choose up to 5)</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {goals.map((g) => {
              const on = selectedGoals.includes(g.slug);
              return (
                <button
                  key={g.slug}
                  data-testid={`q-goal-${g.slug}`}
                  onClick={() => toggleGoal(g.slug)}
                  className={`text-left p-4 rounded-2xl border transition-colors ${on ? "border-[color:var(--vv-teal)] bg-amber-50" : "border-stone-200 bg-white hover:border-amber-200"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{g.name}</div>
                    {on && <CheckCircle2 size={18} className="text-[color:var(--vv-teal)]" />}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex justify-end">
            <button data-testid="q-next-1" disabled={!selectedGoals.length} onClick={() => setStep(2)} className="vv-btn-primary disabled:opacity-40">Continue <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-8 max-w-2xl">
          <h2 className="font-display text-2xl">Tell us a little about you</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <label className="text-sm">Age range
              <select data-testid="q-age" value={profile.age_range} onChange={(e) => setProfile({ ...profile, age_range: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 bg-white">
                <option value="">Select…</option>{AGES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
            <label className="text-sm">Sex assigned at birth
              <select data-testid="q-sex" value={profile.sex_at_birth} onChange={(e) => setProfile({ ...profile, sex_at_birth: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 bg-white">
                <option value="">Select…</option>{SEX.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
            <label className="text-sm">State during consultation
              <select data-testid="q-state" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 bg-white">
                <option value="">Select…</option>{STATES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
            <label className="text-sm md:col-span-2">Primary concern
              <input data-testid="q-primary" value={profile.primary_concern} onChange={(e) => setProfile({ ...profile, primary_concern: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 bg-white" />
            </label>
            <label className="text-sm md:col-span-2">Desired outcome
              <input data-testid="q-outcome" value={profile.desired_outcome} onChange={(e) => setProfile({ ...profile, desired_outcome: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 bg-white" />
            </label>
          </div>
          <div className="mt-6 space-y-2 text-sm">
            {[
              ["has_recent_bloodwork", "I have recent bloodwork"],
              ["on_hormone_therapy",   "I currently receive hormone therapy"],
              ["on_weight_medication", "I currently use weight-management medication"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" data-testid={`q-${k}`} checked={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.checked })} />
                {l}
              </label>
            ))}
          </div>
          <div className="mt-8 flex justify-between">
            <button onClick={() => setStep(1)} className="vv-btn-outline"><ArrowLeft size={16} /> Back</button>
            <button data-testid="q-next-2" onClick={() => setStep(3)} className="vv-btn-primary">Continue <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-8 max-w-2xl">
          <h2 className="font-display text-2xl">Safety screening</h2>
          <p className="text-sm text-stone-600 mt-2">Please tell us anything that applies. This is not a diagnosis — a provider will confirm during your consultation.</p>
          <div className="grid md:grid-cols-2 gap-3 mt-6">
            {SAFETY.map((f) => {
              const on = safetyFlags.includes(f.id);
              return (
                <button
                  key={f.id}
                  data-testid={`q-flag-${f.id}`}
                  onClick={() => toggleFlag(f.id)}
                  className={`text-left px-4 py-3 rounded-xl border ${on ? "border-red-300 bg-red-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                >
                  <div className="flex items-center justify-between">{f.label}{on && <CheckCircle2 size={16} className="text-red-500" />}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex justify-between">
            <button onClick={() => setStep(2)} className="vv-btn-outline"><ArrowLeft size={16} /> Back</button>
            <button data-testid="q-submit" onClick={submit} disabled={submitting} className="vv-btn-primary">{submitting ? "Analyzing…" : "See my options"} <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 4 && results && (
        <div data-testid="q-results" className="mt-8">
          {results.blocked ? (
            <div className="vv-card p-8">
              <h2 className="font-display text-2xl">Provider evaluation recommended first</h2>
              <p className="text-stone-600 mt-3">{results.message}</p>
              <Link to="/request-consultation" data-testid="q-blocked-schedule" className="vv-btn-primary mt-6 inline-flex">Schedule Consultation</Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-3xl">Options a provider may evaluate</h2>
              <p className="text-stone-600 mt-2">This is educational content only. A licensed provider will independently determine any treatment.</p>
              <div className="grid md:grid-cols-3 gap-5 mt-8">
                {(["beginner", "intermediate", "advanced"]).map((tier) => {
                  const tierData = results.tiers[tier] || { items: [], total_starting_price: null };
                  const items = tierData.items || [];
                  return (
                  <div key={tier} data-testid={`q-tier-${tier}`} className={`vv-card p-6 border-t-4 flex flex-col ${tier === "beginner" ? "border-t-amber-600" : tier === "intermediate" ? "border-t-amber-500" : "border-t-stone-900"}`}>
                    <div className="text-xs uppercase tracking-widest text-stone-500">{tier}</div>
                    <div className="font-display text-xl mt-1 capitalize">{tier} pathway</div>
                    {items.length === 0 ? (
                      <div className="mt-6 text-sm text-stone-500">No options match. Discuss with a provider.</div>
                    ) : (
                      <ul className="mt-4 space-y-4">
                        {items.map((r) => (
                          <li key={r.therapy.slug} className="border-t border-stone-100 pt-4">
                            <Link to={`/peptide-library/${r.therapy.slug}`} className="font-semibold text-[color:var(--vv-teal)]">{r.therapy.public_name}</Link>
                            <div className="text-xs text-stone-500 mt-1">{r.reason}</div>
                            {!r.therapy.hide_pricing && (
                              <div className="mt-2">
                                <StartingAtPill price={r.therapy.display_monthly_price} promo={r.therapy.promo_price} testId={`q-price-${r.therapy.slug}`} />
                              </div>
                            )}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <BloodworkBadge level={r.therapy.bloodwork_level} />
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    <TierTotal total={tierData.total_starting_price} testId={`q-tier-total-${tier}`} />
                    <Link to="/request-consultation" data-testid={`q-tier-schedule-${tier}`} className="vv-btn-primary text-sm mt-6 inline-flex self-start">Schedule</Link>
                  </div>
                  );
                })}
              </div>
              <div className="mt-10 grid md:grid-cols-2 gap-4 items-stretch">
                <CallCard
                  location="questionnaire_results"
                  title="Need help deciding?"
                  subtitle="Speak with a VidaVital Medical care coordinator. We can walk through your results and help schedule the right consultation."
                  testId="questionnaire-call-card"
                />
                <Link to="/request-consultation" data-testid="q-results-schedule" className="rounded-2xl border border-amber-100 bg-amber-600 text-white p-6 md:p-7 flex flex-col justify-center hover:bg-amber-700 transition-colors">
                  <div className="text-xs uppercase tracking-widest text-amber-100">Or</div>
                  <div className="font-display text-2xl md:text-3xl mt-2">Schedule your consultation</div>
                  <div className="text-sm text-amber-50 mt-2">A licensed VidaVital provider reviews your goals and history over live video.</div>
                </Link>
              </div>
              <div className="mt-10"><Disclaimer /></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
